import { SdkContext } from '../internal'

export interface NativeStreamingState {
  nativeStreaming: boolean
  /** Whether the conversion infrastructure is configured in this environment. */
  available: boolean
}

export interface MediaCostRow {
  communityId: string
  communityName: string
  nativeStreaming: boolean
  usage: {
    transcodedMinutes: number
    transcodedMinutesByHeight: Record<string, number>
    deliveredGb: number
    deliveryEstimated: boolean
    storageGb: number
  }
  costUsd: { transcode: number; delivery: number; storage: number; total: number }
  marginPercent: number
  chargedUsd: number
}

export interface MediaCostReport {
  month: string
  marginDefined: boolean
  prices: Record<string, number>
  communities: MediaCostRow[]
  totals: { costUsd: number; chargedUsd: number; transcodedMinutes: number; deliveredGb: number }
}

export interface MediaMargin {
  defaultPercent: number
  defined: boolean
  overrides: Record<string, number>
}

export interface MediaPricing {
  prices: Record<string, number>
  margin: MediaMargin
}

/** F-76 part 2, SuperAdmin only (403 to anyone else): native streaming switch, cost report, prices and margin. Never shown to a community. */
export class AdminMediaResource {
  constructor(private ctx: SdkContext) {}

  async getNativeStreaming(communityId: string): Promise<NativeStreamingState> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/communities/{communityId}/native-streaming' }, { communityId })
  }

  async setNativeStreaming(communityId: string, enabled: boolean): Promise<NativeStreamingState> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/admin/communities/{communityId}/native-streaming', hasBody: true }, { communityId, body: { enabled } })
  }

  async costReport(params: { month?: string; communityId?: string } = {}): Promise<MediaCostReport> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/media/cost-report' }, { query: params })
  }

  /** Queues one existing post video (never converted) for adaptive streaming; the community's switch must be on (409 otherwise). */
  async convert(mediaId: string): Promise<{ id: string; processingStatus: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/admin/media/{id}/convert' }, { id: mediaId })
  }

  async getPricing(): Promise<MediaPricing> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/media/pricing' })
  }

  async setPricing(prices: Record<string, number>): Promise<MediaPricing> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/admin/media/pricing', hasBody: true }, { body: { prices } })
  }

  /** Without `communityId`: the global margin. With it: that community's exception (`null` removes it). */
  async setMargin(marginPercent: number | null, communityId?: string): Promise<{ margin: MediaMargin }> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/admin/media/margin', hasBody: true }, { body: { marginPercent, ...(communityId && { communityId }) } })
  }
}
